/**
 * Derived from svg-path-properties@0.2.2 vendored by flubber@0.4.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */

const T_VALUES = [
  -0.06405689286260562, 0.06405689286260562, -0.1911188674736163,
  0.1911188674736163, -0.315042679696163, 0.315042679696163, -0.433793507626045,
  0.433793507626045, -0.545421471388839, 0.545421471388839, -0.648093651936975,
  0.648093651936975, -0.740124191578554, 0.740124191578554, -0.820001985973902,
  0.820001985973902, -0.886415527004401, 0.886415527004401, -0.938274552002732,
  0.938274552002732, -0.974728555971309, 0.974728555971309, -0.995187219997021,
  0.995187219997021,
] as const;

const C_VALUES = [
  0.127938195346752, 0.127938195346752, 0.125837456346828, 0.125837456346828,
  0.121670472927803, 0.121670472927803, 0.115505668053725, 0.115505668053725,
  0.107444270115965, 0.107444270115965, 0.0976186521041138, 0.0976186521041138,
  0.0861901615319532, 0.0861901615319532, 0.0733464814110803,
  0.0733464814110803, 0.0592985849154367, 0.0592985849154367,
  0.0442774388174198, 0.0442774388174198, 0.0285313886289336,
  0.0285313886289336, 0.0123412297999871, 0.0123412297999871,
] as const;

type Point = {
  x: number;
  y: number;
};

type SegmentProperties = {
  x: number;
  y: number;
  tangentX: number;
  tangentY: number;
};

type QuadraticPointTuple = [number, number, number];
type CubicPointTuple = [number, number, number, number];
type ArcLengthFn = (xs: number[], ys: number[], t?: number) => number;
type PointFn = (xs: number[], ys: number[], t: number) => Point;

type BezierDerivative = PointFn;

function quadraticDerivative(
  xs: QuadraticPointTuple,
  ys: QuadraticPointTuple,
  t: number,
): Point {
  return {
    x: (1 - t) * 2 * (xs[1] - xs[0]) + t * 2 * (xs[2] - xs[1]),
    y: (1 - t) * 2 * (ys[1] - ys[0]) + t * 2 * (ys[2] - ys[1]),
  };
}

function cubicDerivative(
  xs: CubicPointTuple,
  ys: CubicPointTuple,
  t: number,
): Point {
  return quadraticPoint(
    [3 * (xs[1] - xs[0]), 3 * (xs[2] - xs[1]), 3 * (xs[3] - xs[2])],
    [3 * (ys[1] - ys[0]), 3 * (ys[2] - ys[1]), 3 * (ys[3] - ys[2])],
    t,
  );
}

function t2length(
  length: number,
  totalLength: number,
  func: ArcLengthFn,
  xs: number[],
  ys: number[],
) {
  let error = 1;
  let t = length / totalLength;
  let step = (length - func(xs, ys, t)) / totalLength;

  while (error > 0.001) {
    const increasedTLength = func(xs, ys, t + step);
    const decreasedTLength = func(xs, ys, t - step);
    const increasedTError = Math.abs(length - increasedTLength) / totalLength;
    const decreasedTError = Math.abs(length - decreasedTLength) / totalLength;

    if (increasedTError < error) {
      error = increasedTError;
      t += step;
    } else if (decreasedTError < error) {
      error = decreasedTError;
      t -= step;
    } else {
      step /= 2;
    }
  }

  return t;
}

function quadraticPoint(
  xs: QuadraticPointTuple,
  ys: QuadraticPointTuple,
  t: number,
): Point {
  return {
    x: (1 - t) * (1 - t) * xs[0] + 2 * (1 - t) * t * xs[1] + t * t * xs[2],
    y: (1 - t) * (1 - t) * ys[0] + 2 * (1 - t) * t * ys[1] + t * t * ys[2],
  };
}

function cubicPoint(
  xs: CubicPointTuple,
  ys: CubicPointTuple,
  t: number,
): Point {
  return {
    x:
      (1 - t) * (1 - t) * (1 - t) * xs[0] +
      3 * (1 - t) * (1 - t) * t * xs[1] +
      3 * (1 - t) * t * t * xs[2] +
      t * t * t * xs[3],
    y:
      (1 - t) * (1 - t) * (1 - t) * ys[0] +
      3 * (1 - t) * (1 - t) * t * ys[1] +
      3 * (1 - t) * t * t * ys[2] +
      t * t * t * ys[3],
  };
}

function getQuadraticArcLength(xs: number[], ys: number[], t = 1) {
  const ax = xs[0] - 2 * xs[1] + xs[2];
  const ay = ys[0] - 2 * ys[1] + ys[2];
  const bx = 2 * xs[1] - 2 * xs[0];
  const by = 2 * ys[1] - 2 * ys[0];
  const A = 4 * (ax * ax + ay * ay);
  const B = 4 * (ax * bx + ay * by);
  const C = bx * bx + by * by;

  if (A === 0) {
    return t * Math.sqrt((xs[2] - xs[0]) ** 2 + (ys[2] - ys[0]) ** 2);
  }

  const b = B / (2 * A);
  const c = C / A;
  const u = t + b;
  const k = c - b * b;

  return (
    (Math.sqrt(A) / 2) *
    (u * Math.sqrt(u * u + k) -
      b * Math.sqrt(b * b + k) +
      k *
        Math.log(
          Math.abs((u + Math.sqrt(u * u + k)) / (b + Math.sqrt(b * b + k))),
        ))
  );
}

function getCubicArcLength(xs: number[], ys: number[], t = 1) {
  const z = t / 2;
  let sum = 0;

  for (let index = 0; index < 20; index += 1) {
    const correctedT = z * T_VALUES[index] + z;
    const derivative = cubicDerivative(
      xs as CubicPointTuple,
      ys as CubicPointTuple,
      correctedT,
    );
    sum +=
      C_VALUES[index] *
      Math.sqrt(derivative.x * derivative.x + derivative.y * derivative.y);
  }

  return z * sum;
}

class Bezier {
  readonly a: Point;

  readonly b: Point;

  readonly c: Point;

  readonly d: Point;

  private readonly getArcLength: ArcLengthFn;

  private readonly getPoint: PointFn;

  private readonly getDerivative: BezierDerivative;

  private length: number;

  constructor(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx?: number,
    dy?: number,
  ) {
    this.a = { x: ax, y: ay };
    this.b = { x: bx, y: by };
    this.c = { x: cx, y: cy };
    this.d = { x: dx ?? 0, y: dy ?? 0 };

    if (dx !== null && dx !== undefined && dy !== null && dy !== undefined) {
      this.getArcLength = getCubicArcLength;
      this.getPoint = cubicPoint as PointFn;
      this.getDerivative = cubicDerivative as BezierDerivative;
    } else {
      this.getArcLength = getQuadraticArcLength;
      this.getPoint = quadraticPoint as PointFn;
      this.getDerivative = quadraticDerivative as BezierDerivative;
    }

    this.length = 0;
    this.init();
  }

  init() {
    this.length = this.getArcLength(
      [this.a.x, this.b.x, this.c.x, this.d.x],
      [this.a.y, this.b.y, this.c.y, this.d.y],
    );
  }

  getTotalLength() {
    return this.length;
  }

  getPointAtLength(length: number): Point {
    const t = t2length(
      length,
      this.length,
      this.getArcLength,
      [this.a.x, this.b.x, this.c.x, this.d.x],
      [this.a.y, this.b.y, this.c.y, this.d.y],
    );

    return this.getPoint(
      [this.a.x, this.b.x, this.c.x, this.d.x],
      [this.a.y, this.b.y, this.c.y, this.d.y],
      t,
    );
  }

  getTangentAtLength(length: number): Point {
    const t = t2length(
      length,
      this.length,
      this.getArcLength,
      [this.a.x, this.b.x, this.c.x, this.d.x],
      [this.a.y, this.b.y, this.c.y, this.d.y],
    );
    const derivative = this.getDerivative(
      [this.a.x, this.b.x, this.c.x, this.d.x],
      [this.a.y, this.b.y, this.c.y, this.d.y],
      t,
    );
    const mdl = Math.sqrt(
      derivative.x * derivative.x + derivative.y * derivative.y,
    );
    return mdl > 0
      ? { x: derivative.x / mdl, y: derivative.y / mdl }
      : { x: 0, y: 0 };
  }

  getPropertiesAtLength(length: number): SegmentProperties {
    const tangent = this.getTangentAtLength(length);
    const point = this.getPointAtLength(length);
    return { x: point.x, y: point.y, tangentX: tangent.x, tangentY: tangent.y };
  }
}

export default function createBezier(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx?: number,
  dy?: number,
) {
  return new Bezier(ax, ay, bx, by, cx, cy, dx, dy);
}
